import queryString from 'query-string';
import {camelizeKeys} from 'humps';
import cookies from 'js-cookie';
import TokenManager from '@setapp/auth-tokens-manager';
import Authenticator from '@setapp/customer-authenticator';
import UiElement from '../../../shared/ui-element/ui-element';
import config from '../../../shared/config/config';
import analytics, {events} from '../../../shared/analytics';
import request from '../../utils/request/request';
import SignupFormStepSpeciality from './signup-form-step-speciality';
import SignupFormStepEmail from './signup-form-step-email';
import SignupFormStepPassword from './signup-form-step-password';
import SignupFormStepName from './signup-form-step-name';
import Logger from '../../utils/logger/logger';

import './signup-form.scss';

const REGISTRATION_FLOW_SINGLE_APP = 'appLanding';
const REGISTRATION_FLOW_BUNDLE = 'bundleLanding';
const DEFAULT_TIER_TYPE = 'regular_monthly';

/*
* Redirect timeout for analytics
* */
const REDIRECT_TIMEOUT = 300;
const STEP_ANIMATION_TIMEOUT = 150;

class SignupFormManager extends UiElement {
  ui = {
    socialButtonsBlock: '.js-social-buttons',
  };

  uiCollections = {
    socialSignupButtons: '.js-social-signup-button',
    formSteps: '.js-signup-form-step',
  };

  events = {
    'click @uiCollections.socialSignupButtons': 'handleSocialButtonClick',
  };

  signupParams = {};

  signupFormStepNames = {
    speciality: 'speciality',
    email: 'email',
    password: 'password',
    name: 'name',
  };

  constructor() {
    super({rootElement: '#signup-form-manager'});

    const cookiesDomain = config.get('cookiesDomain');
    const tokenManager = new TokenManager({
      cookiesDomain,
      secureCookies: window.location.protocol === 'https:',
      rememberPeriod: 365,
      accessTokenName: 'customer_access_token',
      refreshTokenName: 'customer_refresh_token',
    });

    this.authenticator = new Authenticator({
      apiRoot: config.get('customerAccountAPI.root'),
      cookiesDomain,
      tokenManager,
    });

    this.specialityStep = document.getElementById('signup-form-step-speciality');

    if (this.specialityStep) {
      this.signupFormStepSpeciality = new SignupFormStepSpeciality({
        onSubmit: this.handleSpecialityFormSubmit,
      });

      this.sideAppsBlock = document.querySelector('.js-signup-side-apps-block');
      this.primaryContainer = document.querySelector('.signup-modal__primary-container');
      this.signupModal = document.getElementById('signup-modal');
      this.appsIcons = document.querySelectorAll('.signup-modal__signup-app-image');

      this.shouldRedirectToNewBundlePage = false;
    }

    const emailStepParams = {onSubmit: this.handleEmailFormSubmit};

    if (this.specialityStep) {
      emailStepParams.onBackButtonClick = this.handleBackButtonStepEmailClick;
    }

    this.signupFormStepEmail = new SignupFormStepEmail(emailStepParams);
    this.signupFormStepPassword = new SignupFormStepPassword({
      onSubmit: this.handlePasswordFormSubmit,
      onBackButtonClick: this.handleBackButtonStepPasswordClick,
      onRetypeButtonClick: this.handleRetypeButtonClick,
    });
    this.signupFormStepName = new SignupFormStepName({
      onSubmit: this.handleUsernameFormSubmit,
      onBackButtonClick: this.handleBackButtonStepNameClick,
      onRetypeButtonClick: this.handleRetypeButtonClick,
    });
  }

  onInit({signupParams = {}, signupHideSocials, campaign} = {}) {
    super.onInit();
    if (this.specialityStep) {
      this.signupFormStepSpeciality.init();
    }
    this.signupFormStepEmail.init();
    this.signupFormStepPassword.init();
    this.signupFormStepName.init();

    const registrationFlow = this.getRegistrationFlow();
    if (registrationFlow) {
      this.signupParams = {
        registration_flow: registrationFlow,
        ...this.signupParams,
      };
    }

    if (campaign) {
      this.signupParams = {
        ...this.signupParams,
        campaign,
      };
    }

    const pricingTestSegment = cookies.get('regularPricingTest');
    const defaultTierType = pricingTestSegment === 'new' ? 'test_regular_1_mac_monthly' : DEFAULT_TIER_TYPE;

    this.signupParams = {
      ...this.signupParams,
      ...signupParams,
      /**
       * always check for a tier_type and set a default value if none passed to prevent form from sending tier_type
       * taken from the previous opening
       */
      tier_type: signupParams.tier_type || defaultTierType,
    };

    if (signupHideSocials) {
      this.hideSocialButtons();
    }

    this.updateSocialSignupParams(this.signupParams);

    analytics.trackEvent(events.SIGNUP_FORM_OPEN);
  }

  destroy() {
    super.destroy();

    this.showSocialButtons();
  }

  getSignupData() {
    // TODO: support ab tests segments
    return {
      // merge manually passed params first to prevent form values overwriting
      ...this.signupParams,
    };
  }

  updateSignupParams(newParams) {
    this.signupParams = {
      ...this.signupParams,
      ...newParams,
    };
  }

  showSignupFormStep = (stepName) => {
    this.currentStep = stepName;
    this.rootElement.classList.toggle('signup-form-step_faded');

    setTimeout(() => {
      this.uiCollections.formSteps.forEach((step) => {
        if (step.dataset.stepName === stepName) {
          step.classList.remove('d-none');
        } else {
          step.classList.add('d-none');
        }
      });
      this.rootElement.classList.toggle('signup-form-step_faded');
    }, STEP_ANIMATION_TIMEOUT);
  };

  updateEmailPlaceholders(email) {
    this.signupFormStepPassword.updateEmailPlaceholder(email);
    this.signupFormStepName.updateEmailPlaceholder(email);
  }

  handleSocialButtonClick (e) {
    e.preventDefault();
    const socialAnalyticEvent = e.target.dataset.analyticEventAction;

    analytics.trackEvent(events[socialAnalyticEvent]);
    setTimeout(() => {
      window.location.assign(e.target.href);
    }, REDIRECT_TIMEOUT);
  }

  handleBackButtonStepEmailClick = () => {
    setTimeout(() => {
      this.sideAppsBlock.classList.add('d-none');
      this.primaryContainer.classList.add('signup-modal__speciality');
      this.signupModal.classList.add('signup-modal__onboarding');
    }, STEP_ANIMATION_TIMEOUT);

    this.showSignupFormStep(this.signupFormStepNames.speciality);
  };

  handleBackButtonStepPasswordClick = () => {
    this.showSignupFormStep(this.signupFormStepNames.email);
  };

  handleBackButtonStepNameClick = () => {
    this.showSignupFormStep(this.signupFormStepNames.password);
  };

  handleRetypeButtonClick = () => {
    this.showSignupFormStep(this.signupFormStepNames.email);
  };

  handleSpecialityFormSubmit = (data) => {
    const params = data.bundleId ? {
      registration_flow: {
        type: 'bundleLanding',
        params: {bundleId: data.bundleId},
      },
    } : null;

    this.updateSignupParams(params);

    if (data.bundleId) {
      this.appsIcons.forEach((app) => {
        app.classList.remove('d-none');
      });

      this.showBundleApps(data.bundleId);

      setTimeout(() => {
        this.sideAppsBlock.classList.remove('d-none');
      }, STEP_ANIMATION_TIMEOUT);

      this.shouldRedirectToNewBundlePage = true;
      this.signupModal.classList.add('signup-modal__onboarding');

      const bundleTitle = this.findBundle(data.bundleId).title;

      analytics.trackEvent({
        ...events.SIGNUP_FORM_SPECIALITY_STEP,
        eventLabel: bundleTitle,
      });
    } else {
      this.signupModal.classList.remove('signup-modal__onboarding');
      analytics.trackEvent({
        ...events.SIGNUP_FORM_SPECIALITY_STEP,
        eventLabel: 'Skip',
      });
    }

    setTimeout(() => {
      this.primaryContainer.classList.remove('signup-modal__speciality');
    }, STEP_ANIMATION_TIMEOUT);
    this.showSignupFormStep(this.signupFormStepNames.email);
  };

  handleEmailFormSubmit = (data) => {
    this.updateSignupParams({email: data.email});
    this.updateEmailPlaceholders(data.email);
    this.showSignupFormStep(this.signupFormStepNames.password);
  };

  handlePasswordFormSubmit = (data) => {
    this.updateSignupParams({password: data.password});
    this.showSignupFormStep(this.signupFormStepNames.name);
  };

  handleUsernameFormSubmit = (data) => {
    this.updateSignupParams({
      name: data.name,
      marketing_subscribed: data.marketing_subscribed,
      terms_accepted: data.terms_accepted,
    });
    this.submitSignup();
  };

  submitSignup() {
    const data = this.getSignupData();
    const locale = this.rootElement.dataset.language;

    return this.authenticator.signUp(data, {language: locale})
      .then(() => this.handleSignUpSuccess(data))
      .catch((e) => {
        this.handleSignUpError(e);
      });
  }

  handleSignUpSuccess = signUpData => this.fetchUserProfile()
    .then((user) => {
      analytics.trackEvent({
        ...events.SIGN_UP_SUCCESS,
        userEmail: user.email,
        userId: user.id,
      });

      if (signUpData.marketing_subscribed) {
        analytics.trackEvent(events.SIGN_UP_EMAIL_OPT_IN);
      }

      setTimeout(() => {
        if (this.shouldRedirectToCustomerAccount(user)) {
          if (this.rootElement.dataset.signupOnboardingTestVariant === '1withOnboardind' && this.shouldRedirectToNewBundlePage) {
            window.location.assign(config.get('customerAccountPages.successfulRegistrationBundle'));
          } else if (this.rootElement.dataset.singleAppFinalPageTestVariant === '1new') {
            window.location.assign(config.get('customerAccountPages.successfulRegistrationAppLanding'));
          } else {
            window.location.assign(config.get('customerAccountPages.successfulRegistration'));
          }
        } else {
          window.location.assign(this.rootElement.dataset.nextPage);
        }
      }, REDIRECT_TIMEOUT);
    });

  handleSignUpError = (errors) => {
    this.signupFormStepName.enableSubmitBtn();

    errors.getAll().forEach((error) => {
      const errorMessage = error.message || this.rootElement.dataset.defaultError;

      if (error.field === 'email') {
        this.signupFormStepEmail.showEmailFieldError(errorMessage);
        this.showSignupFormStep('email');
      } else if (error.field === 'name') {
        this.signupFormStepName.showFieldError('name', errorMessage);
      } else {
        Logger.catchError('Signup error', error);
      }
    });
  };

  fetchUserProfile() {
    return request.get(config.get('customerAccountAPI.account'));
  }

  shouldRedirectToCustomerAccount(user) {
    const isSingleAppRegistrationFlow = user.registration_flow === REGISTRATION_FLOW_SINGLE_APP;
    const isBundleRegistrationFlow = user.registration_flow === REGISTRATION_FLOW_BUNDLE;

    return user.payment_info_required || isSingleAppRegistrationFlow || isBundleRegistrationFlow;
  }

  hideSocialButtons() {
    this.ui.socialButtonsBlock.classList.add('d-none');
  }

  showSocialButtons() {
    this.ui.socialButtonsBlock.classList.remove('d-none');
  }

  updateSocialSignupParams(params) {
    this.uiCollections.socialSignupButtons.forEach((element) => {
      const {signupParams = null, ...restQuery} = queryString.parse(element.search);

      // eslint-disable-next-line no-param-reassign
      element.search = queryString.stringify({
        ...restQuery,
        signupParams: JSON.stringify({
          ...JSON.parse(signupParams),
          ...camelizeKeys(params),
        }),
      });
    });
  }

  getRegistrationFlow() {
    return JSON.parse(this.rootElement.dataset.registrationFlow || null);
  }

  showBundleApps(bundleId) {
    const bundle = this.findBundle(bundleId);

    const bundleApps = bundle.apps;

    this.appsIcons.forEach((app) => {
      if (!bundleApps.includes(parseInt(app.dataset.appId, 10))) {
        app.classList.add('d-none');
      }
    });
  }

  findBundle(id) {
    const allBundles = config.get('bundleApps');
    return allBundles.find(bundle => bundle.id === parseInt(id, 10));
  }
}

export default SignupFormManager;
